import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy, Eye, FileText, Image as ImageIcon, ClipboardPaste, Plus, Search, Trash2 } from "lucide-react";
import { rhm, apiError, dOnly, dt } from "../api";
import { useRhmAuth } from "../RhmAuthContext";
import { Badge, Btn, Card, Empty, Field, Input, Modal, PageHead, Select, StatCard, Table, Td, Textarea } from "../ui";
import { MediaLibrary } from "./MediaLibrary";
import { LiveSwitch } from "../components/LiveSwitch";
import QuickBlog from "./QuickBlog";

const TABS = [
    { key: "posts", label: "ALL POSTS" },
    { key: "categories", label: "CATEGORIES" },
    { key: "authors", label: "AUTHORS" },
    { key: "analytics", label: "ANALYTICS" },
    { key: "settings", label: "BLOG SETTINGS" },
];
const STATUS_FILTERS = ["all", "published", "draft", "review", "scheduled", "unpublished", "archived"];
const STATUS_TONE = { published: "good", draft: "muted", review: "gold", scheduled: "gold",
    unpublished: "warn", archived: "danger" };

export default function RhmBlog() {
    const navigate = useNavigate();
    const qc = useQueryClient();
    const { can } = useRhmAuth();
    const [tab, setTab] = useState("posts");
    const [status, setStatus] = useState("all");
    const [q, setQ] = useState("");
    const [showNew, setShowNew] = useState(false);
    const [showQuick, setShowQuick] = useState(false);
    const [showMedia, setShowMedia] = useState(false);

    const { data: stats } = useQuery({ queryKey: ["blog-stats"], queryFn: async () => (await rhm.get("/blog/stats")).data });
    const { data: meta } = useQuery({ queryKey: ["blog-meta-crm"], queryFn: async () => (await rhm.get("/blog/meta")).data });
    const { data: posts = [] } = useQuery({
        queryKey: ["blog-posts-crm", status, q],
        queryFn: async () => (await rhm.get("/blog/posts", { params: { status, q: q || undefined } })).data,
    });

    const act = async (fn, msg) => {
        try {
            await fn();
            toast.success(msg);
            qc.invalidateQueries({ queryKey: ["blog-posts-crm"] });
            qc.invalidateQueries({ queryKey: ["blog-stats"] });
        } catch (e) { toast.error(apiError(e)); }
    };

    return (
        <div data-testid="rhm-blog-page">
            <PageHead title="Blog" sub="Write, optimise and publish articles without leaving the CRM"
                action={
                    <div className="flex flex-wrap gap-2">
                        <Btn variant="ghost" onClick={() => setShowMedia(true)} data-testid="blog-media-library">
                            <ImageIcon className="w-3.5 h-3.5 mr-1 inline" /> MEDIA LIBRARY
                        </Btn>
                        {can("blog.create") && (
                            <Btn variant="ghost" onClick={() => setShowQuick(true)} data-testid="blog-quick-new">
                                <ClipboardPaste className="w-3.5 h-3.5 mr-1 inline" /> QUICK BLOG (PASTE)
                            </Btn>
                        )}
                        {can("blog.create") && (
                            <Btn onClick={() => setShowNew(true)} data-testid="blog-create-new">
                                <Plus className="w-3.5 h-3.5 mr-1 inline" /> CREATE NEW BLOG
                            </Btn>
                        )}
                    </div>
                } />

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
                <StatCard label="Total blogs" value={stats?.total ?? "—"} testId="blog-stat-total" />
                <StatCard label="Published" value={stats?.published ?? "—"} tone="good" testId="blog-stat-published" />
                <StatCard label="Drafts" value={stats?.draft ?? "—"} testId="blog-stat-drafts" />
                <StatCard label="Scheduled" value={stats?.scheduled ?? "—"} tone="gold" testId="blog-stat-scheduled" />
                <StatCard label="Total views" value={stats?.views ?? "—"} testId="blog-stat-views" />
            </div>

            <div className="flex flex-wrap gap-1 border-b border-white/10 mb-4">
                {TABS.map((t) => (
                    <button key={t.key} onClick={() => setTab(t.key)} data-testid={`blog-tab-${t.key}`}
                        className={`mono text-[10px] tracking-[0.18em] px-4 py-3 border-b-2 transition-colors ${
                            tab === t.key ? "border-gold text-gold" : "border-transparent text-zinc-500 hover:text-white"}`}>
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "posts" && (
                <Card title="Articles">
                    <div className="flex flex-wrap items-end gap-3 mb-4">
                        <Field label="Search" className="flex-1 min-w-[200px]">
                            <div className="relative">
                                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                                <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9"
                                    placeholder="Title or URL" data-testid="blog-search" />
                            </div>
                        </Field>
                        <Field label="Status" className="w-44">
                            <Select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="blog-status-filter"
                                options={STATUS_FILTERS.map((s) => ({ value: s, label: s === "all" ? "All statuses" : s }))} />
                        </Field>
                    </div>

                    <Table head={["Article", "Category", "Author", "Status", "Live", "SEO", "Views", ""]} testId="blog-table"
                        empty={!posts.length && <Empty msg="No articles yet. Use CREATE NEW BLOG to write your first one." />}>
                        {posts.map((p) => (
                            <tr key={p.id} data-testid={`blog-row-${p.slug}`}>
                                <Td>
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-12 h-12 shrink-0 bg-bg0 border border-white/10 overflow-hidden">
                                            {p.featured_image?.url
                                                ? <img src={p.featured_image.url} alt="" className="w-full h-full object-cover" />
                                                : <FileText className="w-4 h-4 text-zinc-600 m-4" />}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-semibold truncate max-w-[280px]">{p.title}</div>
                                            <div className="mono text-[9px] tracking-[0.14em] text-zinc-500">
                                                /{p.slug} · {p.published_at ? dOnly(p.published_at) : "NOT PUBLISHED"}
                                                {p.scheduled_for ? ` · GOES LIVE ${dt(p.scheduled_for)}` : ""}
                                            </div>
                                        </div>
                                    </div>
                                </Td>
                                <Td className="text-sm text-zinc-400">{p.category?.name || "—"}</Td>
                                <Td className="text-sm text-zinc-400">{p.author?.name || "—"}</Td>
                                <Td><Badge tone={STATUS_TONE[p.status] || "muted"}>{(p.status || "").toUpperCase()}</Badge></Td>
                                <Td>
                                    <LiveSwitch live={p.page_live !== false} testId={`blog-live-${p.slug}`}
                                        previewHref={`/blog/${p.slug}`} disabled={!can("blog.edit_own")}
                                        onChange={(live) => act(() => rhm.post(`/blog/posts/${p.id}/live`, { live }),
                                            live ? "Article is live again" : "Article switched off")} />
                                </Td>
                                <Td>
                                    <span className={`font-display text-lg ${
                                        p.seo_score >= 80 ? "text-lime" : p.seo_score >= 55 ? "text-orange-300" : "text-red-400"}`}>
                                        {p.seo_score}
                                    </span>
                                </Td>
                                <Td className="text-sm text-zinc-400">{p.views}</Td>
                                <Td>
                                    <div className="flex items-center gap-2 justify-end">
                                        <Btn variant="ghost" onClick={() => navigate(`/rhm/blog/${p.id}`)}
                                            data-testid={`blog-edit-${p.slug}`}>EDIT</Btn>
                                        {p.status === "published" && (
                                            <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer"
                                                data-testid={`blog-view-${p.slug}`} className="text-zinc-500 hover:text-gold">
                                                <Eye className="w-4 h-4" />
                                            </a>
                                        )}
                                        {can("blog.create") && (
                                            <button title="Duplicate" data-testid={`blog-duplicate-${p.slug}`}
                                                onClick={() => act(() => rhm.post(`/blog/posts/${p.id}/duplicate`), "Draft copy created")}
                                                className="text-zinc-500 hover:text-white"><Copy className="w-4 h-4" /></button>
                                        )}
                                        {can("blog.archive") && p.status !== "archived" && (
                                            <button title="Archive" data-testid={`blog-archive-${p.slug}`}
                                                onClick={() => act(() => rhm.post(`/blog/posts/${p.id}/status`, { status: "archived" }), "Article archived")}
                                                className="mono text-[9px] tracking-[0.14em] text-zinc-500 hover:text-orange-300">ARCHIVE</button>
                                        )}
                                        {can("blog.delete") && (
                                            <button title="Delete" data-testid={`blog-delete-${p.slug}`}
                                                onClick={() => window.confirm(`Delete “${p.title}” permanently?`)
                                                    && act(() => rhm.delete(`/blog/posts/${p.id}`), "Article deleted")}
                                                className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                                        )}
                                    </div>
                                </Td>
                            </tr>
                        ))}
                    </Table>

                    {!!stats?.most_viewed?.length && (
                        <div className="mt-6 grid md:grid-cols-2 gap-6 pt-5 border-t border-white/10">
                            <div>
                                <div className="mono text-[10px] tracking-[0.2em] text-zinc-500 mb-3">MOST VIEWED</div>
                                {stats.most_viewed.map((p) => (
                                    <div key={p.slug} className="flex justify-between gap-3 text-sm py-1.5">
                                        <span className="truncate">{p.title}</span>
                                        <span className="text-zinc-500">{p.views}</span>
                                    </div>
                                ))}
                            </div>
                            <div>
                                <div className="mono text-[10px] tracking-[0.2em] text-zinc-500 mb-3">RECENTLY UPDATED</div>
                                {(stats.recently_updated || []).map((p) => (
                                    <div key={p.slug} className="flex justify-between gap-3 text-sm py-1.5">
                                        <span className="truncate">{p.title}</span>
                                        <span className="text-zinc-500">{dOnly(p.updated_at)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </Card>
            )}

            {tab === "categories" && <CategoriesPanel meta={meta} canManage={can("blog.categories_manage")} />}
            {tab === "authors" && <AuthorsPanel meta={meta} canManage={can("blog.authors_manage")} />}
            {tab === "analytics" && <AnalyticsPanel />}
            {tab === "settings" && <SettingsPanel meta={meta} canManage={can("blog.settings_manage")} />}

            {showNew && <NewPostDialog meta={meta} onClose={() => setShowNew(false)}
                onCreated={(id) => navigate(`/rhm/blog/${id}`)} />}
            {showQuick && <QuickBlog onClose={() => setShowQuick(false)}
                onCreated={(id) => navigate(`/rhm/blog/${id}`)} />}
            {showMedia && <MediaLibrary folder="Blog" onClose={() => setShowMedia(false)}
                canDelete={can("blog.media_delete")}
                onPick={() => { setShowMedia(false); toast.info("Pick images from inside the article editor to place them."); }} />}
        </div>
    );
}

const NewPostDialog = ({ meta, onClose, onCreated }) => {
    const [form, setForm] = useState({ title: "", category_id: "", author_id: "" });
    const create = async () => {
        try {
            const { data } = await rhm.post("/blog/posts", form);
            toast.success("Draft created");
            onCreated(data.id);
        } catch (e) { toast.error(apiError(e)); }
    };
    return (
        <Modal open onClose={onClose} title="Create new blog" testId="new-post-dialog">
            <div className="space-y-4">
                <Field label="Article title" required hint="You can change this any time">
                    <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                        data-testid="new-post-title" placeholder="10 Natural Ways to Support Healthy Digestion" />
                </Field>
                <Field label="Category">
                    <Select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                        data-testid="new-post-category">
                        <option value="">Choose later</option>
                        {(meta?.categories || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </Select>
                </Field>
                <Field label="Published by">
                    <Select value={form.author_id} onChange={(e) => setForm({ ...form, author_id: e.target.value })}
                        data-testid="new-post-author">
                        <option value="">Choose later</option>
                        {(meta?.authors || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </Select>
                </Field>
                <div className="flex justify-end gap-2">
                    <Btn variant="ghost" onClick={onClose}>CANCEL</Btn>
                    <Btn onClick={create} disabled={!form.title.trim()} data-testid="new-post-submit">START WRITING</Btn>
                </div>
            </div>
        </Modal>
    );
};

const CategoriesPanel = ({ meta, canManage }) => {
    const qc = useQueryClient();
    const [form, setForm] = useState(null);
    const refresh = () => qc.invalidateQueries({ queryKey: ["blog-meta-crm"] });
    const save = async () => {
        try {
            if (form.id) await rhm.patch(`/blog/categories/${form.id}`, form);
            else await rhm.post("/blog/categories", form);
            toast.success("Category saved");
            setForm(null);
            refresh();
        } catch (e) { toast.error(apiError(e)); }
    };
    return (
        <Card title="Categories" data-testid="blog-categories-panel"
            action={canManage && <Btn onClick={() => setForm({ name: "", slug: "", description: "", is_active: true })}
                data-testid="category-new"><Plus className="w-3.5 h-3.5 mr-1 inline" /> CATEGORY</Btn>}>
            <Table head={["Category", "URL", "Visible", ""]} testId="category-table"
                empty={!(meta?.categories || []).length && <Empty msg="No categories yet." />}>
                {(meta?.categories || []).map((c) => (
                    <tr key={c.id} data-testid={`category-row-${c.slug}`}>
                        <Td>
                            <div className="font-semibold">{c.name}</div>
                            <div className="text-xs text-zinc-500">{c.description}</div>
                        </Td>
                        <Td className="mono text-[10px] text-zinc-500">/blog/category/{c.slug}</Td>
                        <Td><Badge tone={c.is_active === false ? "muted" : "good"}>
                            {c.is_active === false ? "HIDDEN" : "VISIBLE"}</Badge></Td>
                        <Td>
                            <div className="flex gap-2 justify-end">
                                {canManage && <Btn variant="ghost" onClick={() => setForm(c)}
                                    data-testid={`category-edit-${c.slug}`}>EDIT</Btn>}
                                {canManage && (
                                    <button data-testid={`category-delete-${c.slug}`} className="text-zinc-500 hover:text-red-400"
                                        onClick={async () => {
                                            if (!window.confirm(`Delete “${c.name}”?`)) return;
                                            try { await rhm.delete(`/blog/categories/${c.id}`); refresh(); toast.success("Category deleted"); }
                                            catch (e) { toast.error(apiError(e)); }
                                        }}><Trash2 className="w-4 h-4" /></button>
                                )}
                            </div>
                        </Td>
                    </tr>
                ))}
            </Table>
            {form && (
                <Modal open onClose={() => setForm(null)} title={form.id ? "Edit category" : "New category"} testId="category-dialog">
                    <div className="space-y-4">
                        <Field label="Name" required>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                data-testid="category-name" />
                        </Field>
                        <Field label="Description" hint="Shown at the top of the category page">
                            <Textarea rows={2} value={form.description || ""}
                                onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="category-description" />
                        </Field>
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={form.is_active !== false} data-testid="category-active"
                                onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Visible on the website
                        </label>
                        <div className="flex justify-end gap-2">
                            <Btn variant="ghost" onClick={() => setForm(null)}>CANCEL</Btn>
                            <Btn onClick={save} disabled={!form.name} data-testid="category-save">SAVE</Btn>
                        </div>
                    </div>
                </Modal>
            )}
        </Card>
    );
};

const AuthorsPanel = ({ meta, canManage }) => {
    const qc = useQueryClient();
    const [form, setForm] = useState(null);
    const refresh = () => qc.invalidateQueries({ queryKey: ["blog-meta-crm"] });
    const save = async () => {
        try {
            if (form.id) await rhm.patch(`/blog/authors/${form.id}`, form);
            else await rhm.post("/blog/authors", form);
            toast.success("Author saved");
            setForm(null);
            refresh();
        } catch (e) { toast.error(apiError(e)); }
    };
    const blank = { name: "", title: "", photo_url: "", bio: "", qualifications: "", expertise: "",
        registration: "", clinic: "", city: "", links: [], is_brand: false, is_active: true };
    return (
        <Card title="Authors" data-testid="blog-authors-panel"
            action={canManage && <Btn onClick={() => setForm(blank)} data-testid="author-new">
                <Plus className="w-3.5 h-3.5 mr-1 inline" /> AUTHOR</Btn>}>
            <Table head={["Author", "Title", "Type", "Status", ""]} testId="author-table"
                empty={!(meta?.authors || []).length && <Empty msg="No authors yet." />}>
                {(meta?.authors || []).map((a) => (
                    <tr key={a.id} data-testid={`author-row-${a.id}`}>
                        <Td>
                            <div className="flex items-center gap-3">
                                {a.photo_url
                                    ? <img src={a.photo_url} alt={a.name} className="w-9 h-9 rounded-full object-cover" />
                                    : <div className="w-9 h-9 rounded-full bg-gold/15 text-gold grid place-items-center font-display">{a.name[0]}</div>}
                                <div>
                                    <div className="font-semibold">{a.name}</div>
                                    <div className="text-xs text-zinc-500">{a.qualifications}</div>
                                </div>
                            </div>
                        </Td>
                        <Td className="text-sm text-zinc-400">{a.title}</Td>
                        <Td><Badge tone={a.is_brand ? "gold" : "muted"}>{a.is_brand ? "BRAND" : "PERSON"}</Badge></Td>
                        <Td><Badge tone={a.is_active === false ? "muted" : "good"}>
                            {a.is_active === false ? "INACTIVE" : "ACTIVE"}</Badge></Td>
                        <Td>
                            <div className="flex gap-2 justify-end">
                                {canManage && <Btn variant="ghost" onClick={() => setForm(a)} data-testid={`author-edit-${a.id}`}>EDIT</Btn>}
                            </div>
                        </Td>
                    </tr>
                ))}
            </Table>
            {form && (
                <Modal open onClose={() => setForm(null)} wide title={form.id ? "Edit author" : "New author"} testId="author-dialog">
                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Name" required>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="author-name" />
                        </Field>
                        <Field label="Professional title">
                            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="author-title" />
                        </Field>
                        <Field label="Photo URL">
                            <Input value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} data-testid="author-photo" />
                        </Field>
                        <Field label="Qualifications">
                            <Input value={form.qualifications} onChange={(e) => setForm({ ...form, qualifications: e.target.value })} data-testid="author-qualifications" />
                        </Field>
                        <Field label="Areas of expertise">
                            <Input value={form.expertise} onChange={(e) => setForm({ ...form, expertise: e.target.value })} data-testid="author-expertise" />
                        </Field>
                        <Field label="Registration number">
                            <Input value={form.registration} onChange={(e) => setForm({ ...form, registration: e.target.value })} data-testid="author-registration" />
                        </Field>
                        <Field label="Clinic / practice" hint="Shown in the author box under each article">
                            <Input value={form.clinic || ""} onChange={(e) => setForm({ ...form, clinic: e.target.value })} data-testid="author-clinic" />
                        </Field>
                        <Field label="City">
                            <Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="author-city" />
                        </Field>
                        <div className="md:col-span-2">
                            <Field label="Short biography">
                                <Textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="author-bio" />
                            </Field>
                        </div>
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={!!form.is_brand} data-testid="author-is-brand"
                                onChange={(e) => setForm({ ...form, is_brand: e.target.checked })} /> This is the ReHealthMe brand identity
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={form.is_active !== false} data-testid="author-is-active"
                                onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active
                        </label>
                    </div>
                    <div className="mt-5 flex justify-end gap-2">
                        <Btn variant="ghost" onClick={() => setForm(null)}>CANCEL</Btn>
                        <Btn onClick={save} disabled={!form.name} data-testid="author-save">SAVE AUTHOR</Btn>
                    </div>
                </Modal>
            )}
        </Card>
    );
};

const AnalyticsPanel = () => {
    const { data } = useQuery({ queryKey: ["blog-analytics"], queryFn: async () => (await rhm.get("/blog/analytics")).data });
    const rows = data?.conversion_rows || [];
    const label = { product_click: "Product clicks", consultation_click: "Consultation clicks", cta_click: "CTA clicks" };
    return (
        <div className="space-y-4" data-testid="blog-analytics-panel">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <StatCard label="Article views" value={data?.views ?? "—"} testId="analytics-views" />
                <StatCard label="Product clicks" value={data?.product_clicks ?? "—"} tone="gold" testId="analytics-product-clicks" />
                <StatCard label="Consultation clicks" value={data?.consultation_clicks ?? "—"} tone="good" testId="analytics-consult-clicks" />
                <StatCard label="CTA clicks" value={data?.cta_clicks ?? "—"} testId="analytics-cta-clicks" />
                <StatCard label="Shares" value={data?.shares ?? "—"} testId="analytics-shares" />
            </div>
            <Card title="Blog → business">
                <Table head={["Article", "Action", "Clicks"]} testId="analytics-table"
                    empty={!rows.length && <Empty msg="No product or consultation clicks tracked yet." />}>
                    {rows.map((r, i) => (
                        <tr key={i} data-testid={`analytics-row-${i}`}>
                            <Td className="mono text-[11px]">blog:{r.slug}</Td>
                            <Td className="text-sm text-zinc-400">{label[r.type] || r.type}</Td>
                            <Td className="font-display text-lg text-gold">{r.count}</Td>
                        </tr>
                    ))}
                </Table>
            </Card>
        </div>
    );
};

const SettingsPanel = ({ meta, canManage }) => {
    const qc = useQueryClient();
    const [form, setForm] = useState(null);
    React.useEffect(() => { if (meta?.settings && !form) setForm(meta.settings); }, [meta, form]);
    if (!form) return null;
    const save = async () => {
        try {
            await rhm.put("/blog/settings", form);
            toast.success("Blog settings saved");
            qc.invalidateQueries({ queryKey: ["blog-meta-crm"] });
        } catch (e) { toast.error(apiError(e)); }
    };
    return (
        <Card title="Blog settings" data-testid="blog-settings-panel">
            <div className="grid md:grid-cols-2 gap-4">
                <Field label="Homepage section heading">
                    <Input value={form.homepage_heading} data-testid="settings-homepage-heading"
                        onChange={(e) => setForm({ ...form, homepage_heading: e.target.value })} />
                </Field>
                <Field label="Homepage articles">
                    <Select value={form.homepage_source} data-testid="settings-homepage-source"
                        onChange={(e) => setForm({ ...form, homepage_source: e.target.value })}
                        options={[{ value: "latest", label: "Latest published" },
                            { value: "featured", label: "Featured only" },
                            { value: "category", label: "From one category" }]} />
                </Field>
                {form.homepage_source === "category" && (
                    <Field label="Homepage category">
                        <Select value={form.homepage_category_id} data-testid="settings-homepage-category"
                            onChange={(e) => setForm({ ...form, homepage_category_id: e.target.value })}>
                            <option value="">Choose a category</option>
                            {(meta?.categories || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </Select>
                    </Field>
                )}
                <div className="md:col-span-2">
                    <Field label="Blog intro line">
                        <Textarea rows={2} value={form.blog_intro} data-testid="settings-blog-intro"
                            onChange={(e) => setForm({ ...form, blog_intro: e.target.value })} />
                    </Field>
                </div>
                <div className="md:col-span-2">
                    <Field label="Health content disclaimer" hint="Used on every article unless overridden">
                        <Textarea rows={3} value={form.disclaimer_text} data-testid="settings-disclaimer"
                            onChange={(e) => setForm({ ...form, disclaimer_text: e.target.value })} />
                    </Field>
                </div>
                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.disclaimer_default_on} data-testid="settings-disclaimer-default"
                        onChange={(e) => setForm({ ...form, disclaimer_default_on: e.target.checked })} /> Show disclaimer on new articles
                </label>
                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.toc_default_on} data-testid="settings-toc-default"
                        onChange={(e) => setForm({ ...form, toc_default_on: e.target.checked })} /> Show “In this article” by default
                </label>
                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.sidebar_enabled} data-testid="settings-sidebar"
                        onChange={(e) => setForm({ ...form, sidebar_enabled: e.target.checked })} /> Show the article sidebar
                </label>
                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.newsletter_enabled} data-testid="settings-newsletter"
                        onChange={(e) => setForm({ ...form, newsletter_enabled: e.target.checked })} /> Show newsletter signup
                </label>

                <div className="md:col-span-2 border-t border-white/10 pt-5 mt-2">
                    <div className="mono text-[10px] tracking-[0.2em] text-gold mb-4">AUTHOR BOX UNDER EACH ARTICLE</div>
                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Heading above the author name">
                            <Input value={form.author_box_label || ""} data-testid="settings-author-label"
                                onChange={(e) => setForm({ ...form, author_box_label: e.target.value })} />
                        </Field>
                        <Field label="Reviewer wording">
                            <Input value={form.reviewer_label || ""} data-testid="settings-reviewer-label"
                                onChange={(e) => setForm({ ...form, reviewer_label: e.target.value })} />
                        </Field>
                        <Field label="Who reviews health content" hint="Shown as “reviewed by” with the last reviewed date">
                            <Select value={form.reviewer_author_id || ""} data-testid="settings-reviewer-author"
                                onChange={(e) => setForm({ ...form, reviewer_author_id: e.target.value })}>
                                <option value="">No reviewer line</option>
                                {(meta?.authors || []).filter((a) => !a.is_brand).map((a) => (
                                    <option key={a.id} value={a.id}>{a.name}{a.qualifications ? `, ${a.qualifications}` : ""}</option>
                                ))}
                            </Select>
                        </Field>
                        <div className="space-y-2">
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={form.author_box_enabled !== false} data-testid="settings-author-box"
                                    onChange={(e) => setForm({ ...form, author_box_enabled: e.target.checked })} /> Show the author box
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={form.author_box_show_clinic !== false} data-testid="settings-author-clinic"
                                    onChange={(e) => setForm({ ...form, author_box_show_clinic: e.target.checked })} /> Show clinic and city
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={form.author_box_show_links !== false} data-testid="settings-author-links"
                                    onChange={(e) => setForm({ ...form, author_box_show_links: e.target.checked })} /> Show profile links (from SEO &amp; Google)
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={form.reviewer_show_date !== false} data-testid="settings-reviewer-date"
                                    onChange={(e) => setForm({ ...form, reviewer_show_date: e.target.checked })} /> Show the last reviewed date
                            </label>
                        </div>
                    </div>
                </div>
            </div>
            {canManage && <div className="mt-5"><Btn onClick={save} data-testid="settings-save">SAVE SETTINGS</Btn></div>}
        </Card>
    );
};
